/**
 * Uma janela própria da mesa (docs/spec/popout-windows.md): um pedaço do layout da mesa, com espaços
 * e abas, desenhado como a mesa (`room/Desk.tsx`).
 *
 * A janela é uma instância própria do cliente: entra na mesa com o token já salvo e uma conexão
 * própria (o servidor conta a mesma pessoa uma vez só) e tem os hosts dela (diálogos de plugin,
 * avisos, perguntas, perfil). Música, voz e dados 3D ficam só na janela principal, para nada tocar
 * em dobro; os avisos de chat também (notify.ts).
 *
 * Ela lê o layout que a principal guarda (`layoutKey`) e acompanha as mudanças pelo evento
 * `storage`: a cena aberta pela biblioteca, a conversa que chegou. O que ela muda no layout (trocar,
 * fechar, arrastar, dividir) vai para a principal como uma operação, e a principal grava.
 *
 * Pelo canal das janelas: diz `here` ao abrir e a cada `hello` da principal, `closed` ao sair e
 * `touch` a cada clique num espaço; fecha com `close`, ao sair da mesa, ou quando o pedaço dela some
 * do layout.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage, useT, type Language } from '../i18n/index.js';
import { applyGlobalSettings, loadSettings } from '../settings.js';
import { ProfilePopupHost } from '../social/ProfilePopup.js';
import { useAwayReporter } from '../social/idle.js';
import { useApp } from '../store.js';
import { PromptHost } from '../ui/prompt.js';
import { WebInstallDialog } from '../plugins/web/WebInstallDialog.js';
import { Toaster } from '../ui/Toaster.js';
import { type PopoutSpec } from '../windowMode.js';
import { layoutKey, listPanels, loadLayout, windowView, type DesktopLayout, type LayoutOp } from './layout.js';
import { SheetTitle } from './modules/registry.js';
import { onWindowMessage, postWindows } from './windows.js';
import { roomInitials } from './openRooms.js';
import { Desk } from './Desk.js';
import './room.css';

export function PopoutApp({ spec }: { spec: PopoutSpec }) {
  const t = useT();
  // usar só a janela própria também conta: a pessoa não fica "Ausente" para os amigos
  useAwayReporter();
  const token = useApp((s) => s.token);
  const user = useApp((s) => s.user);
  const room = useApp((s) => s.room);
  const activeRoomId = useApp((s) => s.activeRoomId);
  const status = useApp((s) => s.status);
  const error = useApp((s) => s.error);
  const initialize = useApp((s) => s.initialize);
  const joinRoom = useApp((s) => s.joinRoom);
  const { roomId, windowId } = spec;
  const layout = useStoredLayout(roomId);
  // o pedaço desta janela, no centro (sem as janelas que saíram dela)
  const view = windowView(layout, windowId);
  const shown: DesktopLayout | null = view ? { version: 3, sites: { client: { node: view, size: 0 } } } : null;
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    void initialize();
  }, [initialize]);
  useEffect(() => {
    if (user) joinRoom(roomId);
  }, [user?.id, joinRoom, roomId]);

  // o canal com a janela principal
  useEffect(() => {
    const here = () => postWindows({ t: 'here', roomId, windowId });
    here();
    const off = onWindowMessage((m) => {
      if (m.t === 'hello' && m.roomId === roomId) here();
      else if (m.t === 'close' && m.roomId === roomId && (!m.windowId || m.windowId === windowId)) window.close();
      else if (m.t === 'focus' && m.windowId === windowId) window.focus();
    });
    const bye = () => postWindows({ t: 'closed', roomId, windowId });
    window.addEventListener('pagehide', bye);
    return () => {
      off();
      window.removeEventListener('pagehide', bye);
    };
  }, [roomId, windowId]);

  // saí da mesa (expulso, mesa apagada), ou o pedaço desta janela saiu do layout (voltou para a
  // mesa, ou o último espaço dela fechou, ou tudo foi para outras janelas): não há mais o que mostrar
  const joined = useRef(false);
  if (room) joined.current = true;
  useEffect(() => {
    if (joined.current && !activeRoomId) window.close();
  }, [activeRoomId]);
  const seen = useRef(false);
  const present = !!view;
  useEffect(() => {
    if (present) seen.current = true;
    else if (seen.current) window.close();
  }, [present]);

  // tema, tamanho do chat e idioma trocados na janela principal valem aqui também
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'rpgdesk.settings') {
        const settings = loadSettings();
        applyGlobalSettings(settings);
        useApp.setState({ settings });
      } else if (e.key === 'rpgdesk.language' && (e.newValue === 'pt-BR' || e.newValue === 'en')) useLanguage.setState({ language: e.newValue as Language });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // o que a janela muda no layout vai para a principal, que grava; o clique num espaço faz dele o
  // último usado (lá, para onde vai uma aba nova; aqui, o nome da janela)
  const apply = useCallback((op: LayoutOp) => postWindows({ t: 'layout', roomId, op }), [roomId]);
  const [lastUsed, setLastUsed] = useState<string | null>(null);
  const touch = useCallback(
    (nodeId: string) => {
      setLastUsed(nodeId);
      postWindows({ t: 'touch', roomId, nodeId });
    },
    [roomId],
  );

  // a janela se chama pela aba à vista no último espaço usado nela, como num navegador; vazia, é a
  // "Nova janela". O título da janela do sistema é esse e o da mesa
  const spaces = shown ? listPanels(shown) : [];
  const named = spaces.find((n) => n.id === lastUsed && n.sheets.length) ?? spaces.find((n) => n.sheets.length);
  const namedSheet = named ? (named.sheets.find((s) => s.id === named.active) ?? named.sheets[0]) : undefined;
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const title = titleRef.current?.textContent?.trim();
    document.title = [title, room?.name, 'RPG Desk'].filter(Boolean).join(' — ');
  });

  let body: React.ReactNode = null;
  if (!token) body = <div className="rd-module__placeholder">{t('room.popout.login')}</div>;
  // não entrou (a mesa sumiu, a principal saiu dela): diz por quê, em vez de carregar para sempre
  else if (!room && error) body = <div className="rd-module__placeholder">{error}</div>;
  // entrando, ou esperando a principal pôr a janela no layout
  else if (!room || !shown) body = <div className="rd-room rd-room--loading"><span className="rd-spinner" /> {t('room.entering')}</div>;

  return (
    <div className="app">
      <div className="rd-popout">
        <header className="rd-popout__header">
          {room ? (
            <span className="rd-popout__room" title={room.name}>
              <span className="rd-popout__roomBadge" aria-hidden="true">{roomInitials(room.name)}</span>
              <span className="rd-popout__roomName">{room.name}</span>
            </span>
          ) : null}
          <span ref={titleRef} className="rd-popout__title">{!room ? 'RPG Desk' : namedSheet ? <SheetTitle sheet={namedSheet} /> : t('room.window.new')}</span>
          {status !== 'online' && room ? (
            <span className="rd-popout__status" role="status">
              <span className="rd-room__reconnectingDot" /> {t('room.status.connecting')}
            </span>
          ) : null}
          <button type="button" className="rd-btn rd-btn--secondary rd-popout__back" onClick={() => window.close()}>
            {t('room.popout.back')}
          </button>
        </header>
        {body ?? <Desk layout={layout} shown={shown!} win={windowId} roomId={roomId} apply={apply} editing={editing} onEditing={setEditing} onTouch={touch} className="rd-room--window" />}
      </div>
      {/* antes das perguntas: as confirmações do Bloquear (no perfil) e de atualizar um oficial (no
          catálogo) abrem por cima deles */}
      <ProfilePopupHost />
      <WebInstallDialog />
      <PromptHost />
      <Toaster />
    </div>
  );
}

/** O layout que a janela principal guarda, acompanhando as mudanças dela. */
function useStoredLayout(roomId: string): DesktopLayout {
  const [layout, setLayout] = useState(() => loadLayout(roomId));
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === layoutKey(roomId)) setLayout(loadLayout(roomId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [roomId]);
  return layout;
}
